import { memo } from "react";

import {
  type CartLine,
  type SelectedOption,
} from "@shopify/hydrogen-react/storefront-api-types";

import { CartGiftLineQuantityAdjustButton } from "@cart/CartGiftLineQuantityAdjustButton";
import { CartItemSelectedOptions } from "@cart/CartItemSelectedOptions";
import { Icon } from "@components/Icon";
import { Price } from "@components/Price";
import { Heading } from "@react/components/Heading";
import { Image } from "@react/components/Image";
import { Link } from "@react/components/Link";
import { Text } from "@react/components/Text";
import { tw } from "classnames";

type Props = {
  cartLineUpdating: boolean;
  cost: CartLine["cost"];
  merchandise: CartLine["merchandise"];
  selectedOptions: Partial<SelectedOption>[] | undefined;
  shippingNote: string | null;
};

const styles = {
  container: tw(
    "border",
    "border-solid",
    "border-divider",
    "rounded",
    "bg-white",
    "mt-4",
    "px-2",
    "py-2.5",
    "w-full"
  ),
  headingContainer: tw("flex", "flex-col", "gap-1"),
  contentContainer: tw("flex", "flex-wrap", "animate-fade-in"),
  imageContainer: tw("mr-4", "h-16", "w-16", "bg-background-secondary"),
  detailsContainer: tw("flex", "flex-col", "flex-1"),
  giftWithPurchaseContainer: tw(
    "flex",
    "w-full",
    "gap-2",
    "items-center",
    "justify-between",
    "p-2",
    "mt-4",
    "bg-white",
    "rounded-md",
    "border",
    "border-solid",
    "border-divider"
  ),
  infoTrashContainer: tw("flex"),
  productTitle: tw("text-primary"),
  shippingNote: tw("text-brick-700", "animate-fade-in"),
  lineItemActions: tw("flex", "flex-col", "gap-1", "ml-auto", "self-start"),
  removeButton: tw(
    "bg-transparent",
    "border-0",
    "outline-none",
    "text-char-500"
  ),
  trashIcon: tw("h-4", "w-4"),
  priceQuantityContainer: tw("justify-between", "flex", "mt-auto"),
  price: (cartLineUpdating: boolean) =>
    tw("self-end", "transition-opacity", "text-brick-700", {
      "opacity-50": cartLineUpdating,
      "opacity-100": !cartLineUpdating,
    }),
  cartLinePlaceholder: tw("h-4", "w-8", "animate-pulse", "bg-placeholder"),
};

const CartProductGiftItemViewBase = ({
  cartLineUpdating,
  cost,
  merchandise,
  selectedOptions,
  shippingNote,
}: Props) => {
  return (
    <div className={styles.container}>
      <div className={styles.contentContainer}>
        <div className={styles.imageContainer}>
          <Link to={merchandise.product.onlineStoreUrl ?? "/"} unstyled>
            <Image height={64} src={merchandise.image?.url} width={64} />
          </Link>
        </div>
        <div className={styles.detailsContainer}>
          <div className={styles.infoTrashContainer}>
            <div className={styles.headingContainer}>
              <Heading as="h6" className={styles.productTitle} size="xs">
                <Link to={merchandise.product.onlineStoreUrl ?? "/"} unstyled>
                  {merchandise.product.title}
                </Link>
              </Heading>
              <CartItemSelectedOptions selectedOptions={selectedOptions} />
              {!!shippingNote && (
                <Text as="caption" className={styles.shippingNote}>
                  {shippingNote}
                </Text>
              )}
            </div>
            <div className={styles.lineItemActions}>
              <CartGiftLineQuantityAdjustButton
                adjust="remove"
                className={styles.removeButton}
              >
                <Icon className={styles.trashIcon} name="trash" />
              </CartGiftLineQuantityAdjustButton>
            </div>
          </div>
          <div className={styles.priceQuantityContainer}>
            <div className={styles.price(cartLineUpdating)}>
              <Price as="body2" price={cost.totalAmount} size="sm" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const CartProductGiftItemView = memo(CartProductGiftItemViewBase);
